import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { readPendingTwoFactor, clearPendingTwoFactor, safeRedirect } from '../utils/pendingTwoFactor';
import { appConfigAPI } from '../services/api';
import ErrorBoundary from '../components/ErrorBoundary';
import { assetUrl } from '../utils/assetUrl';

// CSS animations for logo
const logoAnimationStyles = `
  @keyframes spinClockwise {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  
  @keyframes subtleZoom {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(0.95);
    }
  }
  
  .logo-bg-spin {
    animation: spinClockwise 20s linear infinite;
    opacity: 0.15;
    position: absolute;
    z-index: 0;
  }
  
  .logo-front-zoom {
    animation: subtleZoom 3s ease-in-out infinite;
    position: relative;
    z-index: 1;
  }
`;

const Login = () => {
  const navigate = useNavigate();
  const { login, verifyTwoFactor, loginWithGoogle } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [appConfig, setAppConfig] = useState(null);
  // { pendingToken, methods, redirect? } once sign-in requires a code; a Google
  // sign-in on a 2FA account arrives here with it in sessionStorage.
  const [twoFactor, setTwoFactor] = useState(readPendingTwoFactor);
  useEffect(() => { clearPendingTwoFactor(); }, []);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [sessionEnded] = useState(() => {
    try {
      return sessionStorage.getItem('signout_reason') === 'session_ended';
    } catch (storageError) {
      return false;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.removeItem('signout_reason');
    } catch (storageError) {
      // Nothing to clear if the browser refuses storage.
    }
  }, []);

  useEffect(() => {
    // Load app config for branding (public page, so handle errors gracefully)
    appConfigAPI.get().then(res => {
      setAppConfig(res.data);
      const faviconHref = assetUrl(res.data.favicon_url);
      if (faviconHref) {
        const favicon = document.querySelector("link[rel='icon']") || document.createElement('link');
        favicon.rel = 'icon';
        favicon.href = faviconHref;
        if (!document.querySelector("link[rel='icon']")) {
          document.head.appendChild(favicon);
        }
      }
    }).catch(() => {
      // If not authenticated or config doesn't exist, use defaults
      setAppConfig({ app_name: 'eloNest' });
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      navigate('/dashboard');
    } else if (result.requires2FA) {
      setTwoFactor({ pendingToken: result.pendingToken, methods: result.methods });
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await verifyTwoFactor(twoFactor.pendingToken, twoFactorCode);

    if (result.success) {
      navigate(safeRedirect(twoFactor.redirect));
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    setError('');
    setGoogleLoading(true);
    await loginWithGoogle();
    // Note: loginWithGoogle redirects, so we don't need to handle response here
  };

  const appLogoSrc = assetUrl(appConfig?.logo_url);

  return (
    <ErrorBoundary section="Login">
      <>
      <style>{logoAnimationStyles}</style>
      <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
                {appLogoSrc ? (
                  <div className="flex justify-center items-center mb-4 relative" style={{ 
                    minHeight: (appConfig.logo_height || 80) * 1.5,
                    minWidth: (appConfig.logo_width || 200) * 1.5
                  }}>
                    {/* Background spinning symbol - smaller, directly behind logo */}
                    <img 
                      src="/img/eloNestsymbol.png" 
                      alt=""
                      className="logo-bg-spin absolute"
                      style={{ 
                        width: ((appConfig.logo_width || 200) * 1.5) * 0.67,
                        height: ((appConfig.logo_height || 80) * 1.5) * 0.67,
                        objectFit: 'contain',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 0
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Main logo with subtle zoom */}
                    <img 
                      src={appLogoSrc}
                      alt={appConfig.app_name || 'eloNest'}
                      style={{
                        width: (appConfig.logo_width || 200) * 1.5,
                        height: (appConfig.logo_height || 80) * 1.5,
                        objectFit: 'contain',
                        position: 'relative',
                        zIndex: 1
                      }}
                      className="logo-front-zoom"
                    />
                  </div>
                ) : (
                  <div className="flex justify-center items-center mb-4 relative" style={{ 
                    minHeight: '180px',
                    minWidth: '300px'
                  }}>
                    {/* Main logo with subtle zoom */}
                    <img 
                      src="/img/eloNestLogo.png" 
                      alt={appConfig?.app_name || 'eloNest'}
                      className="logo-front-zoom relative"
                      style={{ 
                        height: '180px',
                        width: 'auto',
                        maxWidth: '450px',
                        zIndex: 1
                      }}
                      onError={(e) => {
                        // Fallback if logo doesn't exist
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Background spinning symbol - positioned behind the blue nest portion */}
                    <img 
                      src="/img/eloNestsymbol.png" 
                      alt=""
                      className="logo-bg-spin absolute"
                      style={{ 
                        width: '100px',
                        height: '100px',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%) translateX(-35%)',
                        objectFit: 'contain',
                        zIndex: 0
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
          {appConfig?.app_tagline && (
            <p className="mt-2 text-center text-sm text-gray-500">{appConfig.app_tagline}</p>
          )}
          <p className="mt-2 text-center text-sm text-gray-600">
            {twoFactor ? 'Enter your verification code' : 'Sign in to your account'}
          </p>
        </div>
        {twoFactor ? (
          <form className="mt-8 space-y-6 bg-white p-8 shadow rounded-lg" onSubmit={handleTwoFactorSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 p-4" role="alert">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Enter the 6-digit code from your authenticator app
                {twoFactor.methods?.email ? ', a code we emailed you,' : ''} or a recovery code.
              </p>
              <label htmlFor="twoFactorCode" className="block text-sm font-medium text-gray-700">
                Verification code
              </label>
              <input
                id="twoFactorCode"
                type="text"
                required
                autoFocus
                className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="6-digit code or recovery code"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => { setTwoFactor(null); setTwoFactorCode(''); setError(''); }}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                Back to sign in
              </button>
            </div>
          </form>
        ) : (
        <form className="mt-8 space-y-6 bg-white p-8 shadow rounded-lg" onSubmit={handleSubmit}>
          {sessionEnded && !error && (
            <div className="rounded-md bg-blue-50 p-4" role="status">
              <div className="text-sm text-blue-700">Your session ended. Please sign in again.</div>
            </div>
          )}
          {error && (
            <div className="rounded-md bg-red-50 p-4" role="alert">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                required
                className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-500">
                  Forgot password?
                </Link>
              </div>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m0 0A9.966 9.966 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.736m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || googleLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:bg-gray-400"
              style={{
                backgroundColor: appConfig?.primary_color || '#3B82F6',
                '--tw-ring-color': appConfig?.primary_color || '#3B82F6'
              }}
              onMouseEnter={(e) => {
                if (!loading && !googleLoading) {
                  const color = appConfig?.primary_color || '#3B82F6';
                  e.target.style.backgroundColor = color;
                  e.target.style.filter = 'brightness(0.9)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && !googleLoading) {
                  e.target.style.backgroundColor = appConfig?.primary_color || '#3B82F6';
                  e.target.style.filter = 'none';
                }
              }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading || googleLoading}
                className="w-full flex justify-center items-center gap-3 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
              >
                {googleLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Connecting to Google...</span>
                  </>
                ) : (
                  <>
                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span>Sign in with Google</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="text-center">
            <Link to="/register" className="text-sm text-blue-600 hover:text-blue-500">
              Don't have an account? Register here
            </Link>
          </div>
        </form>
        )}
      </div>
      </div>
      </>
    </ErrorBoundary>
  );
};

export default Login;
